import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import { trackEvent } from '../utils/analytics';
import { formatCompactJobPay } from '../utils/jobPayPresentation';

interface VendorDetail {
  id: number;
  slug: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  city: string;
  state: string;
  specialty: string;
  description: string | null;
  union_affiliated: boolean;
  is_featured: boolean;
  is_claimed: boolean;
  is_domain_confirmed?: boolean;
  is_owner: boolean;
  created_at: string;
}

interface VendorJob {
  id: number;
  source: string;
  title: string;
  company_name: string;
  location: string | null;
  rate: string | null;
  rate_min: number;
  rate_max: number | null;
  rate_type: string;
  job_type: string | null;
  employment_type: string | null;
  source_url: string | null;
  scraped_at: string;
  created_at: string;
  activity_status?: 'open_verified';
  activity_checked_at?: string;
}

interface VendorClaimChallenge {
  id: number;
  domain: string;
  recordName: string;
  recordValue: string;
  expiresAt: string;
}

const SPECIALTY_LABELS: Record<string, string> = {
  union_labor: 'Union Labor',
  av_rental: 'AV Rental',
  audio: 'Audio',
  lighting: 'Lighting',
  video: 'Video',
  staging: 'Staging',
  backline: 'Backline',
  production: 'Production',
  general: 'General',
};

const SPECIALTY_COLORS: Record<string, string> = {
  union_labor: 'bg-blue-100 text-blue-800',
  av_rental: 'bg-purple-100 text-purple-800',
  audio: 'bg-green-100 text-green-800',
  lighting: 'bg-yellow-100 text-yellow-800',
  video: 'bg-red-100 text-red-800',
  staging: 'bg-orange-100 text-orange-800',
  backline: 'bg-pink-100 text-pink-800',
  production: 'bg-indigo-100 text-indigo-800',
  general: 'bg-gray-100 text-gray-700',
};

const SPECIALTIES = [
  'union_labor', 'av_rental', 'backline', 'staging',
  'lighting', 'audio', 'video', 'production', 'general',
];

export default function VendorDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const id = slug; // slug or numeric id — backend handles both
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const [vendor, setVendor] = useState<VendorDetail | null>(null);
  const [vendorBoundary, setVendorBoundary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [jobs, setJobs] = useState<VendorJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  // Edit form state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState({
    name: '', contact_name: '', phone: '', website: '',
    city: '', state: '', specialty: '', description: '', union_affiliated: false,
  });

  // Claim state
  const [claiming, setClaiming] = useState(false);
  const [verifyingClaim, setVerifyingClaim] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimChallenge, setClaimChallenge] = useState<VendorClaimChallenge | null>(null);
  const claimChallengeRef = useRef<HTMLElement | null>(null);

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBoundary, setFollowBoundary] = useState<string | null>(null);
  const [followLoading, setFollowLoading] = useState(false);
  const hasCurrentPrincipal = Boolean(isAuthenticated && user && accessToken);
  const accessBoundary = `${id || 'unknown'}:${
    hasCurrentPrincipal
      ? `${user.id}:${user.role}:${accessToken}`
      : 'anonymous'
  }`;
  const currentAccessBoundary = useRef(accessBoundary);
  currentAccessBoundary.current = accessBoundary;
  const vendorRequestSequence = useRef(0);

  useEffect(() => {
    if (claimChallenge) {
      claimChallengeRef.current?.focus();
    }
  }, [claimChallenge]);
  const followRequestSequence = useRef(0);
  const vendorIsCurrent = vendorBoundary === accessBoundary;
  const currentVendor = vendorIsCurrent ? vendor : null;
  const followIsCurrent = followBoundary === accessBoundary;
  const currentIsFollowing = followIsCurrent ? isFollowing : false;
  const currentFollowerCount = followIsCurrent ? followerCount : 0;
  const currentFollowLoading = followIsCurrent ? followLoading : false;

  useEffect(() => {
    const requestId = ++vendorRequestSequence.current;
    const requestBoundary = accessBoundary;
    const controller = new AbortController();

    const fetchVendor = async () => {
      setVendor(null);
      setVendorBoundary(null);
      setNotFound(false);
      setLoading(true);
      setEditing(false);
      setSaving(false);
      setSaveError('');
      setClaiming(false);
      setVerifyingClaim(false);
      setClaimError('');
      setClaimSuccess(false);
      setClaimChallenge(null);
      try {
        const headers: Record<string, string> = {};
        if (hasCurrentPrincipal) headers['Authorization'] = `Bearer ${accessToken}`;

        const res = await fetch(`${API_URL}/api/vendors/${id}`, {
          headers,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          vendorRequestSequence.current !== requestId ||
          currentAccessBoundary.current !== requestBoundary
        ) {
          return;
        }
        if (res.status === 404) {
          setNotFound(true);
          setVendorBoundary(requestBoundary);
          return;
        }
        const data: VendorDetail = await res.json();
        if (
          controller.signal.aborted ||
          vendorRequestSequence.current !== requestId ||
          currentAccessBoundary.current !== requestBoundary
        ) {
          return;
        }
        const safeData: VendorDetail = {
          ...data,
          is_owner: Boolean(hasCurrentPrincipal && data.is_owner),
          email: hasCurrentPrincipal && data.is_owner ? data.email : null,
        };
        setVendor(safeData);
        setVendorBoundary(requestBoundary);
        setForm({
          name: safeData.name,
          contact_name: safeData.contact_name || '',
          phone: safeData.phone || '',
          website: safeData.website || '',
          city: safeData.city,
          state: safeData.state,
          specialty: safeData.specialty,
          description: safeData.description || '',
          union_affiliated: safeData.union_affiliated,
        });
      } catch {
        if (
          !controller.signal.aborted &&
          vendorRequestSequence.current === requestId &&
          currentAccessBoundary.current === requestBoundary
        ) {
          setNotFound(true);
          setVendorBoundary(requestBoundary);
        }
      } finally {
        if (
          !controller.signal.aborted &&
          vendorRequestSequence.current === requestId &&
          currentAccessBoundary.current === requestBoundary
        ) {
          setLoading(false);
        }
      }
    };
    fetchVendor();
    return () => controller.abort();
  }, [
    id,
    accessToken,
    isAuthenticated,
    user?.id,
    user?.role,
    accessBoundary,
    user,
    hasCurrentPrincipal,
  ]);

  useEffect(() => {
    if (!id) return;
    setJobsLoading(true);
    fetch(`${API_URL}/api/vendors/${id}/jobs`)
      .then(r => r.json())
      .then(data => setJobs(data.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setJobsLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const requestId = ++followRequestSequence.current;
    const requestBoundary = accessBoundary;
    const controller = new AbortController();
    setIsFollowing(false);
    setFollowerCount(0);
    setFollowBoundary(null);
    setFollowLoading(false);
    const headers: Record<string, string> = {};
    if (hasCurrentPrincipal) headers['Authorization'] = `Bearer ${accessToken}`;
    fetch(`${API_URL}/api/vendors/${id}/follow`, {
      headers,
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(data => {
        if (
          controller.signal.aborted ||
          followRequestSequence.current !== requestId ||
          currentAccessBoundary.current !== requestBoundary
        ) {
          return;
        }
        setIsFollowing(Boolean(data.is_following));
        setFollowerCount(Number(data.follower_count) || 0);
        setFollowBoundary(requestBoundary);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [
    id,
    accessToken,
    isAuthenticated,
    user?.id,
    user?.role,
    accessBoundary,
    hasCurrentPrincipal,
  ]);

  const handleFollow = async () => {
    if (!hasCurrentPrincipal) {
      const params = new URLSearchParams({
        role: 'worker',
        redirect: `/vendors/${slug}`,
      });
      navigate(`/register?${params.toString()}`);
      return;
    }
    const requestId = ++followRequestSequence.current;
    const requestBoundary = accessBoundary;
    setFollowLoading(true);
    try {
      const method = currentIsFollowing ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/vendors/${id}/follow`, {
        method,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (
        res.ok &&
        followRequestSequence.current === requestId &&
        currentAccessBoundary.current === requestBoundary
      ) {
        setIsFollowing(Boolean(data.is_following));
        setFollowerCount(Number(data.follower_count) || 0);
        setFollowBoundary(requestBoundary);
      }
    } catch { /* ignore */ }
    finally {
      if (
        followRequestSequence.current === requestId &&
        currentAccessBoundary.current === requestBoundary
      ) {
        setFollowLoading(false);
      }
    }
  };

  const handleClaim = async () => {
    trackEvent('claim_started', 'vendor_claim', currentVendor?.slug || slug);
    if (!hasCurrentPrincipal) {
      const params = new URLSearchParams({
        role: 'employer',
        redirect: `/vendors/${slug}?claim=1`,
      });
      navigate(`/register?${params.toString()}`);
      return;
    }
    if (user?.role !== 'employer') {
      setClaimError('An active employer account is required to verify a vendor listing.');
      return;
    }
    setClaiming(true);
    setClaimError('');
    try {
      const claimSlug = currentVendor?.slug || slug;
      const res = await fetch(`${API_URL}/api/vendors/${claimSlug}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (currentAccessBoundary.current !== accessBoundary) return;
      if (!res.ok) {
        setClaimError(data.message || 'Could not start domain verification');
      } else {
        setClaimChallenge(data.challenge);
      }
    } catch {
      if (currentAccessBoundary.current === accessBoundary) {
        setClaimError('Something went wrong. Please try again.');
      }
    } finally {
      if (currentAccessBoundary.current === accessBoundary) {
        setClaiming(false);
      }
    }
  };

  const handleVerifyClaim = async () => {
    if (!claimChallenge || !currentVendor || user?.role !== 'employer') return;
    setVerifyingClaim(true);
    setClaimError('');
    try {
      const res = await fetch(
        `${API_URL}/api/vendors/${currentVendor.slug}/claim/${claimChallenge.id}/verify`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const data = await res.json();
      if (currentAccessBoundary.current !== accessBoundary) return;
      if (!res.ok) {
        setClaimError(data.message || 'The DNS record could not be verified yet.');
        return;
      }
      setVendor(prev => prev ? {
        ...prev,
        is_claimed: true,
        is_owner: true,
        is_domain_confirmed: true,
      } : prev);
      setClaimChallenge(null);
      setClaimSuccess(true);
      trackEvent('claim_completed', 'vendor_claim', currentVendor.slug);
    } catch {
      if (currentAccessBoundary.current === accessBoundary) {
        setClaimError('Something went wrong. Please try again.');
      }
    } finally {
      if (currentAccessBoundary.current === accessBoundary) {
        setVerifyingClaim(false);
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${API_URL}/api/vendors/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (currentAccessBoundary.current !== accessBoundary) {
        return;
      }
      if (!res.ok) {
        setSaveError(data.message || 'Could not save changes');
      } else {
        setVendor(prev => prev ? { ...prev, ...data } : data);
        setEditing(false);
      }
    } catch {
      if (currentAccessBoundary.current === accessBoundary) {
        setSaveError('Something went wrong. Please try again.');
      }
    } finally {
      if (currentAccessBoundary.current === accessBoundary) {
        setSaving(false);
      }
    }
  };

  if (loading || !vendorIsCurrent) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="h-4 bg-gray-100 rounded w-1/3 mb-8" />
        <div className="h-3 bg-gray-100 rounded w-full mb-2" />
        <div className="h-3 bg-gray-100 rounded w-3/4" />
      </div>
    );
  }

  if (notFound || !currentVendor) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-400">
        <p className="text-lg font-medium">Vendor not found</p>
        <Link to="/vendors" className="text-indigo-600 text-sm mt-2 inline-block hover:underline">
          ← Back to directory
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${currentVendor.name} — ${SPECIALTY_LABELS[currentVendor.specialty] || 'Event Vendor'} in ${currentVendor.city}, ${currentVendor.state} | StageCrew`}
        description={currentVendor.description || `${currentVendor.name} is a ${SPECIALTY_LABELS[currentVendor.specialty] || 'live event vendor'} based in ${currentVendor.city}, ${currentVendor.state}. Listed on StageCrew's vendor directory.`}
        url={`https://stagecrew.app/vendors/${currentVendor.slug}`}
        keywords={`${currentVendor.name}, ${SPECIALTY_LABELS[currentVendor.specialty]}, ${currentVendor.city} event vendors, ${currentVendor.city} ${currentVendor.specialty}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: currentVendor.name,
          description: currentVendor.description || undefined,
          url: currentVendor.website || undefined,
          address: {
            '@type': 'PostalAddress',
            addressLocality: currentVendor.city,
            addressRegion: currentVendor.state,
            addressCountry: 'US',
          },
        }}
      />
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Back link */}
        <Link to="/vendors" className="text-sm text-indigo-600 hover:underline mb-6 inline-block">
          ← Back to directory
        </Link>

        {/* Header card */}
        <div className={`bg-white rounded-2xl border p-8 mb-6 ${currentVendor.is_featured ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'}`}>
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {currentVendor.is_featured && (
              <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                Featured Listing
              </span>
            )}
            {currentVendor.is_claimed ? (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                currentVendor.is_domain_confirmed
                  ? 'text-green-700 bg-green-50'
                  : 'text-gray-600 bg-gray-100'
              }`}>
                {currentVendor.is_domain_confirmed
                  ? 'Business domain confirmed'
                  : 'Managed listing'}
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                Unclaimed
              </span>
            )}
          </div>

          {/* Name + location */}
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{currentVendor.name}</h1>
          <p className="text-gray-500 mb-4">{currentVendor.city}, {currentVendor.state}</p>

          {/* Specialty tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SPECIALTY_COLORS[currentVendor.specialty] || SPECIALTY_COLORS.general}`}>
              {SPECIALTY_LABELS[currentVendor.specialty] || currentVendor.specialty}
            </span>
            {currentVendor.union_affiliated && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                Union Affiliated
              </span>
            )}
          </div>

          {/* Description */}
          {currentVendor.description && (
            <p className="text-gray-600 mb-5 leading-relaxed">{currentVendor.description}</p>
          )}

          {/* Contact info */}
          <div className="flex flex-col gap-1 text-sm text-gray-600">
            {currentVendor.contact_name && <span><strong>Contact:</strong> {currentVendor.contact_name}</span>}
            {currentVendor.phone && <span><strong>Phone:</strong> {currentVendor.phone}</span>}
            {currentVendor.email && <span><strong>Email:</strong> {currentVendor.email}</span>}
            {currentVendor.website && (
              <a href={currentVendor.website} target="_blank" rel="noopener noreferrer"
                className="text-indigo-600 hover:underline font-medium mt-1">
                Visit website →
              </a>
            )}
          </div>

          {!currentVendor.is_claimed && !currentVendor.is_owner && !claimChallenge && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Own or manage this business?</p>
                  <p className="text-sm text-gray-600">Confirm control of the business website domain to manage this free page.</p>
                </div>
                <button
                  onClick={() => {
                    trackEvent('claim_cta_click', 'vendor_claim', 'detail_header');
                    handleClaim();
                  }}
                  disabled={claiming}
                  className="shrink-0 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-600 transition disabled:opacity-50"
                >
                  {claiming ? 'Starting verification...' : 'Verify this page'}
                </button>
              </div>
            </div>
          )}
        </div>

        {searchParams.get('claim') === '1' && !claimChallenge && !claimSuccess && !currentVendor.is_claimed && (
          <p className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" role="status">
            To manage this listing, start the domain verification below. Returning from signup does not claim it automatically.
          </p>
        )}

        {claimChallenge && (
          <section
            ref={claimChallengeRef}
            tabIndex={-1}
            className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-6"
            aria-labelledby="domain-verification-heading"
          >
            <h2 id="domain-verification-heading" className="text-lg font-semibold text-gray-900">
              Confirm the business website domain
            </h2>
            <p className="mt-2 text-sm text-gray-700">
              Add this TXT record in the DNS settings for <strong>{claimChallenge.domain}</strong>.
              This confirms control of the listed domain; it is not legal-identity verification.
            </p>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="font-semibold text-gray-800">TXT record name</dt>
                <dd className="mt-1 break-all rounded bg-white p-3 font-mono text-gray-900">{claimChallenge.recordName}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-800">TXT record value</dt>
                <dd className="mt-1 break-all rounded bg-white p-3 font-mono text-gray-900">{claimChallenge.recordValue}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-gray-600">
              The challenge expires {new Date(claimChallenge.expiresAt).toLocaleString()}. DNS changes can take time to appear.
            </p>
            {claimError && <p className="mt-3 text-sm text-red-700" role="alert">{claimError}</p>}
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleVerifyClaim}
                disabled={verifyingClaim}
                className="rounded-lg bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {verifyingClaim ? 'Checking DNS...' : 'Verify DNS record'}
              </button>
              <button
                type="button"
                onClick={handleClaim}
                disabled={claiming || verifyingClaim}
                className="rounded-lg border border-amber-300 bg-white px-5 py-2 text-sm font-semibold text-gray-800 hover:bg-amber-100 disabled:opacity-50"
              >
                {claiming ? 'Replacing...' : 'Replace challenge'}
              </button>
            </div>
          </section>
        )}

        {claimError && !claimChallenge && (
          <p className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            {claimError}
          </p>
        )}

        {/* Owner actions */}
        {currentVendor.is_owner && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Manage Your Listing</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                >
                  Edit listing
                </button>
              )}
            </div>

            {editing && (
              <form onSubmit={handleSave} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Company name</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Contact name</label>
                    <input type="text" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                    <input type="text" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Website</label>
                    <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                    <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                    <input type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Specialty</label>
                    <select value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                      {SPECIALTIES.map(s => (
                        <option key={s} value={s}>{SPECIALTY_LABELS[s]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input type="checkbox" id="union_affiliated" checked={form.union_affiliated}
                      onChange={e => setForm(f => ({ ...f, union_affiliated: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 rounded" />
                    <label htmlFor="union_affiliated" className="text-sm text-gray-700">Union affiliated</label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>

                {saveError && <p className="text-red-600 text-sm">{saveError}</p>}

                <div className="flex gap-3">
                  <button type="submit" disabled={saving}
                    className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save changes'}
                  </button>
                  <button type="button" onClick={() => { setEditing(false); setSaveError(''); }}
                    className="text-gray-500 text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Current, pay-qualified listings section */}
        {(jobsLoading || jobs.length > 0) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h2 className="font-semibold text-gray-900">
              Current pay-listed jobs {!jobsLoading && <span className="text-gray-400 font-normal text-sm">({jobs.length})</span>}
            </h2>
            <p className="mb-4 mt-1 text-xs text-gray-500">
              Every job below includes an employer-provided amount and pay basis. Employer-board jobs were listed as open when StageCrew last checked; availability can change.
            </p>
            {jobsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {jobs.map(job => (
                  <div key={job.id} className="py-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{job.title}</p>
                      <p className="mt-0.5 text-sm font-semibold text-emerald-700">
                        {formatCompactJobPay(job)}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {job.location && <span>{job.location} · </span>}
                        {job.job_type && <span className="capitalize">{job.job_type.replace(/_/g, ' ')}</span>}
                      </p>
                      {job.activity_status === 'open_verified' && job.activity_checked_at ? (
                        <p className="mt-1 text-xs text-gray-500">
                          Source listed open · Checked {new Date(job.activity_checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      ) : job.source === 'stagecrew' ? (
                        <p className="mt-1 text-xs text-gray-500">Posted directly on StageCrew</p>
                      ) : null}
                    </div>
                    {job.source_url ? (
                      <a
                        href={job.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Review ${job.title} on the original source`}
                        className="shrink-0 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-1.5 rounded-lg transition"
                      >
                        Review listing
                      </a>
                    ) : job.source === 'stagecrew' ? (
                      <Link
                        to={`/jobs/${job.id}`}
                        aria-label={`View ${job.title} on StageCrew`}
                        className="shrink-0 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-1.5 rounded-lg transition"
                      >
                        View listing
                      </Link>
                    ) : (
                      <span className="shrink-0 text-sm text-gray-400 px-4 py-1.5">
                        Listing unavailable
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Follow & Auto-Apply CTA — shown to workers (non-owners) */}
        {!currentVendor.is_owner && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-900 mb-1">Follow this company</h2>
              <p className="text-sm text-gray-600">
                Save this company for quick reference. Following does not currently create match updates.
                {currentFollowerCount > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">{currentFollowerCount} {currentFollowerCount === 1 ? 'worker' : 'workers'} following</span>
                )}
              </p>
            </div>
            <button
              onClick={handleFollow}
              disabled={currentFollowLoading}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 whitespace-nowrap ${
                currentIsFollowing
                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {currentFollowLoading ? '...' : currentIsFollowing ? '✓ Following' : 'Follow'}
            </button>
          </div>
        )}

        {/* Claim CTA — shown when not yet claimed and user is not the owner */}
        {!currentVendor.is_claimed && !currentVendor.is_owner && !claimChallenge && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Is this your business?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Confirm control of the website domain already shown on this listing. After confirmation, you can update supported profile details.
            </p>
            <button
              onClick={handleClaim}
              disabled={claiming}
              className="bg-amber-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 transition disabled:opacity-50"
            >
              {claiming ? 'Starting verification...' : hasCurrentPrincipal ? 'Start domain verification' : 'Sign up to verify'}
            </button>
          </div>
        )}

        {claimSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center" role="status" aria-live="polite">
            <p className="font-semibold text-green-800 mb-1">Business domain confirmed</p>
            <p className="text-sm text-green-700">You can now manage the supported listing details above.</p>
          </div>
        )}
      </div>
    </div>
  );
}
